import { createTheme } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';

/*
 * WCAG 2.2 AA–compliant gradient palette — shared with cash4you-frontend-google-theme.
 * All gradient stops verified for ≥ 4.5:1 contrast with #FFFFFF.
 *
 * PRIMARY_GRADIENT  #1E40AF (8.2:1) → #2563EB (4.9:1)  — royal blue, calm & trustworthy
 */
export const GEMINI_GRADIENT      = 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)';
export const GEMINI_GRADIENT_SOFT = 'linear-gradient(135deg, rgba(30,64,175,0.1) 0%, rgba(37,99,235,0.1) 100%)';
export const BLUE   = '#1D4ED8';   // blue-700  – 6.5:1 on white
export const INDIGO = '#1E40AF';   // blue-800  – 8.2:1 on white

function buildShadows(isLight: boolean): Shadows {
  const opacity = isLight ? 0.08 : 0.3;
  const rgb = '0,0,0';
  const s = (y: number, b: number, o: number) =>
    `0px ${y}px ${b}px rgba(${rgb}, ${(opacity + o).toFixed(2)})`;
  return [
    'none', s(1,3,0), s(2,6,0), s(3,8,0.01), s(4,12,0.01), s(6,16,0.01),
    s(8,20,0.02), s(10,24,0.02), s(12,28,0.02), s(14,32,0.03), s(16,36,0.03),
    s(18,40,0.03), s(20,44,0.04), s(22,48,0.04), s(24,52,0.04), s(26,56,0.05),
    s(28,60,0.05), s(30,64,0.05), s(32,68,0.06), s(34,72,0.06), s(36,76,0.06),
    s(38,80,0.07), s(40,84,0.07), s(42,88,0.07), s(44,92,0.08),
  ] as Shadows;
}

export const getTheme = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';
  return createTheme({
    palette: {
      mode,
      primary: {
        main:         isLight ? '#1D4ED8' : '#93C5FD',
        light:        isLight ? '#2563EB' : '#BFDBFE',
        dark:         isLight ? '#1E40AF' : '#60A5FA',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main:         isLight ? '#4338CA' : '#A5B4FC',
        light:        isLight ? '#6366F1' : '#C7D2FE',
        dark:         isLight ? '#3730A3' : '#818CF8',
        contrastText: '#FFFFFF',
      },
      background: {
        // Cool gray canvas so white paper cards read clearly (was #F8F9FF ≈ paper).
        default: isLight ? '#EDEFF3' : '#0D0D1A',
        paper:   isLight ? '#FFFFFF'  : '#1C1B2E',
      },
      text: {
        primary:   isLight ? '#202124' : '#E8EAED',
        secondary: isLight ? '#5F6368' : '#9AA0A6',
      },
      success: { main: isLight ? '#00A39E' : '#00DFD8', contrastText: '#fff' },
      warning: { main: isLight ? '#F9AB00' : '#FDD663' },
      error:   { main: isLight ? '#D93025' : '#F28B82' },
      divider: isLight ? '#E8EAED' : '#2E2E48',
    },
    typography: {
      fontFamily: '"Plus Jakarta Sans", "Google Sans", Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 13, // keep admin information density
      h1: { fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 600, letterSpacing: '-0.015em' },
      h4: { fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, letterSpacing: '-0.005em' },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      body1: { letterSpacing: '0.01em' },
      body2: { letterSpacing: '0.01em' },
    },
    shape: { borderRadius: 10 }, // slightly tighter than consumer app (12) for admin density
    shadows: buildShadows(isLight),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { background: isLight ? '#F1F3F4' : '#1C1B2E' },
            '&::-webkit-scrollbar-thumb': {
              background: isLight ? '#BDC1C6' : '#5F6368',
              borderRadius: 4,
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { color: 'default', elevation: 0 },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 20,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            '&:hover': { transform: 'translateY(-1px)' },
          },
          containedPrimary: {
            background: isLight ? '#1D4ED8' : '#2563EB',
            color: '#FFFFFF',
            '&:hover': { background: isLight ? '#1E40AF' : '#1D4ED8' },
          },
          outlinedPrimary: { borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px' } },
          sizeSmall: { padding: '4px 14px', fontSize: '0.8125rem' },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            '& fieldset': { borderColor: isLight ? '#E8EAED' : '#2E2E48', borderWidth: '1.5px' },
            '&:hover fieldset': { borderColor: isLight ? '#1A73E8' : '#8AB4F8' },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${isLight ? '#E8EAED' : '#2E2E48'}`,
            boxShadow: isLight ? '0 2px 12px rgba(0,0,0,0.06)' : '0 2px 12px rgba(0,0,0,0.3)',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 14 },
          outlined: {
            backgroundColor: isLight ? '#FFFFFF' : '#1C1B2E',
            boxShadow: isLight
              ? '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)'
              : '0 1px 3px rgba(0, 0, 0, 0.35)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            border: `1px solid ${isLight ? '#E8EAED' : '#2E2E48'}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 20, fontWeight: 500 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 500 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 6, height: 6, backgroundColor: isLight ? '#E8EAED' : '#2E2E48' },
          bar: { borderRadius: 6, background: GEMINI_GRADIENT },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': { color: isLight ? '#1A73E8' : '#8AB4F8' },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: isLight ? '#1A73E8' : '#8AB4F8',
              opacity: 0.7,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      // CSS gap survives Accordion disableGutters `&.Mui-expanded { margin: 0 }`, which
      // otherwise wipes Stack margin-top and makes expanded panels touch the sibling above.
      MuiStack: {
        defaultProps: { useFlexGap: true },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: isLight ? '#5F6368' : '#9AA0A6',
              background: isLight ? '#F7F8FA' : '#0D0D1A',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isLight ? '#E8EAED' : '#2E2E48',
            fontSize: '0.8125rem',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.4)' },
        },
      },
    },
  });
};

// Legacy named export kept so any existing import of `theme` still compiles.
export const theme = getTheme('light');
